/**
 * Audio Position Tracker Service
 * Saves and restores exact playback positions for each module
 * Like Spotify/Audible resume feature
 */

interface SavedPosition {
  moduleId: string;
  courseId: string;
  position: number; // seconds
  duration: number; // seconds
  timestamp: number; // when saved
}

const STORAGE_KEY = 'audio_positions';
const POSITION_TTL = 24 * 60 * 60 * 1000; // 24 hours

class AudioPositionTrackerClass {
  private positions: Map<string, SavedPosition> = new Map();

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Save current playback position
   */
  savePosition(moduleId: string, courseId: string, position: number, duration: number): void {
    try {
      const key = this.getKey(moduleId, courseId);
      const savedPosition: SavedPosition = {
        moduleId,
        courseId,
        position: Math.floor(position),
        duration: Math.floor(duration),
        timestamp: Date.now()
      };

      this.positions.set(key, savedPosition);
      this.persistToStorage();
      
      console.log('💾 [AudioPosition] Saved:', {
        module: moduleId,
        position: this.formatTime(position),
        duration: this.formatTime(duration)
      });
    } catch (error) {
      console.warn('[PositionTracker] Failed to save position (non-critical):', error);
      // Continue without throwing - position tracking is not critical
    }
  }

  /**
   * Get saved position for a module
   */
  getSavedPosition(moduleId: string, courseId: string): SavedPosition | null {
    try {
      const key = this.getKey(moduleId, courseId);
      const saved = this.positions.get(key);

      if (!saved) {
        return null;
      }

      // Check if position is still valid (not expired)
      const age = Date.now() - saved.timestamp;
      if (age > POSITION_TTL) {
        this.positions.delete(key);
        this.persistToStorage();
        return null;
      }

      // Don't resume if position is within first 5 seconds or last 5 seconds
      if (saved.position < 5 || saved.position > saved.duration - 5) {
        return null;
      }

      return saved;
    } catch (error) {
      console.warn('[PositionTracker] Failed to get position (non-critical):', error);
      return null; // Safe fallback
    }
  }

  /**
   * Clear position for a module
   */
  clearPosition(moduleId: string, courseId: string): void {
    const key = this.getKey(moduleId, courseId);
    this.positions.delete(key);
    this.persistToStorage();
  }

  /**
   * Clear all saved positions
   */
  clearAllPositions(): void {
    this.positions.clear();
    this.persistToStorage();
  }

  /**
   * Get all saved positions for a course
   */
  getCoursePositions(courseId: string): SavedPosition[] {
    const coursePositions: SavedPosition[] = [];
    
    this.positions.forEach(position => {
      if (position.courseId === courseId) {
        // Check if not expired
        const age = Date.now() - position.timestamp;
        if (age <= POSITION_TTL) {
          coursePositions.push(position);
        }
      }
    });

    return coursePositions;
  }

  private getKey(moduleId: string, courseId: string): string {
    return `${courseId}:${moduleId}`;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.positions = new Map(Object.entries(data));
        
        // Clean up expired positions
        const now = Date.now();
        let cleaned = false;
        this.positions.forEach((position, key) => {
          if (now - position.timestamp > POSITION_TTL) {
            this.positions.delete(key);
            cleaned = true;
          }
        });
        
        if (cleaned) {
          this.persistToStorage();
        }

        console.log('📂 [AudioPosition] Loaded positions:', this.positions.size);
      }
    } catch (error) {
      console.error('Failed to load audio positions:', error);
      this.positions = new Map();
    }
  }

  private persistToStorage(): void {
    try {
      const data = Object.fromEntries(this.positions);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save audio positions:', error);
    }
  }

  private formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

export const audioPositionTracker = new AudioPositionTrackerClass();
